import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';

import { PaginatedResultModel, PaginationSearchModel } from '@data-layer/models/common';
import {
  ManagementRouteModel,
  ManagementUpdateVoyageModel,
  ManagementUpdateVoyagePriceListModel,
  ManagementVoyageModel,
  ManagementVoyageSearchModel,
} from '@data-layer/models/management';
import { RouteModel, VoyageModel } from '@data-layer/models/lcl';
import { VoyageCountByStateModel } from '@app/modules/management/voyages-management/models';

@Injectable({
  providedIn: 'root',
})
export class VoyagesRepository {
  constructor(private apiService: ApiService) {}

  public search(params?: ManagementVoyageSearchModel, isShowLoader?: boolean): Observable<PaginatedResultModel<VoyageModel>> {
    return this.apiService.get<PaginatedResultModel<VoyageModel>>(environment.url.freight.voyages, { params }, { isShowLoader });
  }

  public searchByRouteId(
    route: RouteModel['id'],
    params: PaginationSearchModel,
    isShowLoader?: boolean,
  ): Observable<PaginatedResultModel<VoyageModel>> {
    return this.apiService.get<PaginatedResultModel<VoyageModel>>(
      `${environment.url.freight.voyages}`,
      {
        params: {
          ...params,
          route,
        },
      },
      {
        isShowLoader,
      },
    );
  }

  public searchManagement(
    params?: ManagementVoyageSearchModel,
    isShowLoader?: boolean,
  ): Observable<PaginatedResultModel<ManagementVoyageModel>> {
    return this.apiService.get<PaginatedResultModel<ManagementVoyageModel>>(
      environment.url.freight.voyagesManagement,
      { params },
      { isShowLoader },
    );
  }

  public getManagement(id: ManagementVoyageModel['id'], isShowLoader?: boolean): Observable<ManagementVoyageModel> {
    return this.apiService.get<ManagementVoyageModel>(`${environment.url.freight.voyagesManagement}/${id}`, null, { isShowLoader });
  }

  public createManagement(
    freightRouteId: ManagementRouteModel['id'],
    model: ManagementUpdateVoyageModel,
    isShowLoader?: boolean,
  ): Observable<ManagementVoyageModel> {
    return this.apiService.post<ManagementVoyageModel>(
      `${environment.url.freight.routesManagement}/${freightRouteId}/voyages`,
      model,
      null,
      {
        isShowLoader,
      },
    );
  }

  public splitManagementVoyage(voyageId: number, isShowLoader?: boolean): Observable<ManagementVoyageModel> {
    return this.apiService.post<ManagementVoyageModel>(`${environment.url.freight.voyagesManagement}/${voyageId}/split`, null, null, {
      isShowLoader,
    });
  }

  public updateManagement(
    id: ManagementVoyageModel['id'],
    model: ManagementUpdateVoyageModel,
    isShowLoader?: boolean,
  ): Observable<ManagementVoyageModel> {
    return this.apiService.put<ManagementVoyageModel>(`${environment.url.freight.voyagesManagement}/${id}`, model, null, { isShowLoader });
  }

  public deleteManagement(id: ManagementVoyageModel['id'], isShowLoader?: boolean): Observable<never> {
    return this.apiService.delete(`${environment.url.freight.voyagesManagement}/${id}`, null, { isShowLoader });
  }

  public moveShipmentFromManagementVoyage(voyageId: number, shipmentId: number): Observable<any> {
    return this.apiService.patch(`${environment.url.freight.voyagesManagement}/${voyageId}/shipment`, shipmentId);
  }

  public updatePriceList(
    id: ManagementVoyageModel['id'],
    model: ManagementUpdateVoyagePriceListModel,
    isShowLoader?: boolean,
  ): Observable<ManagementVoyageModel> {
    return this.apiService.put<ManagementVoyageModel>(`${environment.url.freight.voyagesManagement}/${id}/pricelists`, model, null, {
      isShowLoader,
    });
  }

  public getVoyageCountStats(): Observable<VoyageCountByStateModel[]> {
    return this.apiService.get<VoyageCountByStateModel[]>(`${environment.url.freight.voyagesManagement}/count-by-status`, null);
  }
}
